import axios from "axios";

class AjaxLoader {
    constructor(trigger, container, filters = []) {
        this.trigger = trigger;
        this.container = container;
        this.filters = filters;
        this.activeFilter = [];
        this.activePeriod = null;
        this.mode = null;
        this.currentPage = 1;
        this.perPage = 6;
        this.offset = 0;
        this.totalPages = 1;
        this.errorClass = "error";
        this.loadingClass = "loading";
        this.noMorePagesClass = "disabled";
        this.url = window.location;
        this.wait = false;
        this.registerEventHandlers();
    }

    registerEventHandlers() {
        this.trigger.addEventListener("click", this.loadNext.bind(this));

        if (this.trigger.dataset.loadmoreMode) {
            this.mode = this.trigger.dataset.loadmoreMode;
        }
        if (this.filters.length) {
            // console.log("Registering filter triggers");
            const reset = document.querySelector(
                "[data-loadmore-filter-reset]"
            );

            this.resetTrigger = reset;
            this.resetTrigger.addEventListener("change", (event) => {
                if (!event.target.checked) {
                    // If the new state is unchecked, then the previous state is checked. Ignore.
                    event.target.checked = true;
                    return;
                } else {
                    // If the new state is checked, then the previous state is unchecked. Reset all filters.
                    this.resetFilters();
                    this.disableFilters();
                    this.onFilterUpdate();
                }
            });

            this.filters.forEach((trigger) => {
                if (trigger.dataset.loadmoreFilterId) {
                    trigger.addEventListener("change", (event) => {
                        if (this.wait) {
                            return;
                        } else {
                            this.wait = true;
                            this.disableFilters();
                        }

                        if (event.target.checked) {
                            // Add new filter to list
                            const id = event.target.dataset.loadmoreFilterId;
                            this.activeFilter = [...this.activeFilter, id];
                        } else {
                            // Remove filter from list (if exists)
                            const id = event.target.dataset.loadmoreFilterId;
                            this.activeFilter = this.activeFilter.filter(
                                (filterId) => filterId !== id
                            );
                        }

                        if (this.activeFilter.length) {
                            reset.checked = false;
                        }
                        this.onFilterUpdate();
                    });
                }
            });
        }
        const select = document.querySelector(
            `[data-loadmore-filter-select="${
                this.trigger.dataset.loadmoreHandle
            }"]`
        );
        if (select) {
            select.addEventListener("change", (event) => {
                if (event.target.selectedIndex === 0) {
                    this.activePeriod = null;
                } else {
                    this.activePeriod = event.target.value;
                }
                this.onFilterUpdate().then(() =>
                    window.dispatchEvent(new Event("swiper-init"))
                );
            });
        }
    }

    getTriggers() {
        if (this.resetTrigger) {
            return [this.resetTrigger, ...this.filters];
        } else {
            return this.filters;
        }
    }
    disableFilters() {
        this.getTriggers().forEach((trigger) => (trigger.disabled = true));
    }

    enableFilters() {
        this.getTriggers().forEach((trigger) => (trigger.disabled = false));
    }

    resetFilters() {
        this.filters.forEach((trigger) => (trigger.checked = false));
        this.activeFilter = [];
    }

    onFilterUpdate() {
        // Empty the container
        this.container.innerHTML = "";

        // Reset the current page
        this.currentPage = 0;
        this.totalPages = 1;

        let perPage = this.perPage;
        let offset = this.offset;

        this.offset = 0;
        this.perPage = offset;
        this.trigger.classList.remove(this.noMorePagesClass);

        return this.loadNext().then(() => {
            // Revert back the offset and perPage
            this.offset = offset;
            this.perPage = perPage;

            this.wait = false;
            this.enableFilters();
        });
    }

    showLoading() {
        this.trigger.classList.add(this.loadingClass);
    }

    stopLoading() {
        this.trigger.classList.remove(this.loadingClass);
    }

    loadNext() {
        if (!this.hasMorePages()) {
            return;
        }

        this.trigger.classList.remove(this.errorClass);
        this.showLoading();

        return this.fetchNext()
            .then((response) => {
                this.totalPages = parseInt(
                    response.headers["x-loadmore-totalpages"]
                );
                this.insertHtml(response.data);
                this.currentPage += 1;
            })
            .catch(() => {
                this.trigger.classList.add(this.errorClass);
                this.stopLoading();
            })
            .finally(() => {
                if (this.hasMorePages() === false) {
                    this.trigger.classList.add(this.noMorePagesClass);
                }

                this.stopLoading();
            });
    }

    hasMorePages() {
        return !(this.currentPage > this.totalPages);
    }

    insertHtml(html) {
        this.container.insertAdjacentHTML("beforeend", html);
    }

    fetchNext() {
        return axios.get(this.url, {
            params: {
                page: this.currentPage,
                skip: this.offset,
                perPage: this.perPage,
                filter: this.activeFilter,
                period: this.activePeriod,
                mode: this.mode,
            },
        });
    }

    setOffset(offset) {
        this.offset = offset;
    }

    setPaginationParam(paramName) {
        this.paginationParam = paramName;
    }

    setPerPage(perPage) {
        this.perPage = perPage;
    }

    setUrl(url) {
        this.url = url;
    }

    setLoadingClass(loadingClass) {
        this.loadingClass = loadingClass;
    }

    setNoMorePagesClass(noMorePagesClass) {
        this.noMorePagesClass = noMorePagesClass;
    }

    setErrorClass(errorClass) {
        this.errorClass = errorClass;
    }
}

function initialize() {
    return [...document.querySelectorAll("[data-loadmore]")]
        .map((trigger) => {
            let container;
            let filterTriggers;

            if (trigger.dataset["loadmoreHandle"]) {
                container = document.querySelector(
                    `[data-loadmore-container=${
                        trigger.dataset.loadmoreHandle
                    }]`
                );
                filterTriggers = document.querySelectorAll(
                    `[data-loadmore-filter="${trigger.dataset.loadmoreHandle}"]`
                );
            } else {
                console.error("Attribute 'data-loadmore-handle' is required");
                return;
            }

            const loader = new AjaxLoader(trigger, container, filterTriggers);

            if (trigger.dataset["loadmoreOffset"]) {
                loader.setOffset(trigger.dataset.loadmoreOffset);
            }

            if (trigger.dataset["loadmoreCurrentPage"]) {
                loader.setCurrentPage(trigger.dataset.loadmoreCurrentPage);
            }
            if (trigger.dataset["loadmorePerPage"]) {
                loader.setPerPage(trigger.dataset.loadmorePerPage);
            }

            if (trigger.dataset["loadmorePageParam"]) {
                loader.setPaginationParam(trigger.dataset.loadmorePageParam);
            }

            if (trigger.dataset["loadmoreUrl"]) {
                loader.setUrl(trigger.dataset.loadmoreUrl);
            }

            if (trigger.dataset["loadmoreLoadingClass"]) {
                loader.setLoadingClass(trigger.dataset.loadmoreLoadingClass);
            }

            if (trigger.dataset["loadmoreNoMorePagesClass"]) {
                loader.setNoMorePagesClass(
                    trigger.dataset.loadmoreNoMorePagesClass
                );
            }

            if (trigger.dataset["loadmoreErrorClass"]) {
                loader.setErrorClass(trigger.dataset.loadmoreErrorClass);
            }

            return loader;
        })
        .filter((l) => l); // Removes null values
}

export default {
    init: () => {
        initialize();
    },
};
